import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Bookmark,
    Check,
    ChevronDown,
    Loader2,
    Pencil,
    Plus,
    Star,
    Trash2,
    X,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { FirstRunTemplateWizard } from "@/components/reelflo/FirstRunTemplateWizard";
import api from "@/lib/api";

const ENDPOINT = "/account/workflow-presets";
const DEFAULT_ENDPOINT = "/account/default-template";

// The 8 workspace fields a template captures — the exact set compared for the
// "• Edited" badge and re-applied on a (re)selection.
const SNAPSHOT_KEYS = [
    "broll_on", "broll_pct", "remove_silence", "silence_pace",
    "caption_template", "music_enabled", "music_selected_id", "music_volume",
];

const byNameAsc = (a, b) =>
    (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase());

const detailMessage = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (d && typeof d === "object" && d.message) return d.message;
    if (typeof d === "string") return d;
    return fallback;
};
const detailCode = (e) => e?.response?.data?.detail?.code || "";

const snapshotEqual = (a, b) => {
    if (!a || !b) return false;
    return SNAPSHOT_KEYS.every((k) => (a[k] ?? null) === (b[k] ?? null));
};

/**
 * Compact control that lets a creator pick a saved workflow template from an
 * in-app dropdown MENU (never a native <select>). Choosing a template applies
 * it IMMEDIATELY — there is no Apply button. The control also auto-loads the
 * account's DEFAULT template once on mount, tracks a "NAME • Edited" state when
 * the live workspace diverges from the selected template's snapshot, and opens
 * the naming wizard for Create-New / Edit. Applying / saving a template only
 * touches workspace UI state (via onApply) or the template store — it NEVER
 * uploads a video, creates a job, reserves minutes, or calls Submagic.
 */
export const WorkflowPresetControl = ({
    settings,
    onApply,
    disabled = false,
    defaultTemplateId = null,
    onDefaultChanged,
    reloadSignal = 0,
    wizardCatalog = {},
    // Compact-Dashboard integration. When compact=true the control renders as
    // ONE thin, full-width template bar directly below Monthly Usage: the
    // entire bar is the DropdownMenu trigger (icon + "Template" label +
    // selected name + default indicator + chevron). The "New" pill, the yellow
    // "• Edited" pill, the inline "Save changes" button, and the wizard-based
    // dropdown "Edit" item are hidden; the compact menu instead carries an
    // "Edit template" item that fires onInlineEdit so the Dashboard opens its
    // existing INLINE edit view (never the wizard). onActiveChange lifts the
    // currently-selected template's { id, name, revision, settings, isDefault }
    // to the parent so inline saves can PATCH against the correct revision (or
    // null when nothing is selected). saveEditsSignal + the onSaveEditsResult
    // callback let the parent drive the existing owner-scoped revision-aware
    // PATCH without duplicating the network path here.
    compact = false,
    onActiveChange = null,
    onInlineEdit = null,
    saveEditsSignal = 0,
    onSaveEditsResult = null,
}) => {
    const [presets, setPresets] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [busy, setBusy] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [deleteFor, setDeleteFor] = useState(null);   // preset pending delete-confirm
    const [replaceFor, setReplaceFor] = useState(null); // { preset, others } needing a replacement default
    const [wizard, setWizard] = useState({
        open: false, mode: "create", editId: null, editRevision: null,
        initialSettings: null, initialName: "",
    });

    // Auto-apply of the default happens exactly once; a monotonic apply token
    // enforces latest-selection-wins if applies ever overlap.
    const autoAppliedRef = useRef(false);
    const applySeqRef = useRef(0);

    const selected = presets.find((p) => p.id === selectedId) || null;
    const edited = selected ? !snapshotEqual(settings, selected.settings) : false;

    // Lift the current active template to the parent for the compact strip and
    // for the inline-save PATCH path. Fires when the effective selection or its
    // stored revision changes; a stable identity for the setter is expected.
    useEffect(() => {
        if (typeof onActiveChange !== "function") return;
        if (!selected) { onActiveChange(null); return; }
        onActiveChange({
            id: selected.id,
            name: selected.name,
            revision: selected.revision,
            settings: selected.settings,
            isDefault: selected.id === defaultTemplateId,
        });
        // Depend on the individual selected fields so a settings mutation that
        // keeps the same id/revision does not re-fire on unrelated re-renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected?.id, selected?.name, selected?.revision, selected?.settings, defaultTemplateId, onActiveChange]);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(ENDPOINT);
            const list = ((data && data.presets) || []).slice().sort(byNameAsc);
            setPresets(list);
            setSelectedId((prev) => (list.some((p) => p.id === prev) ? prev : ""));
            return list;
        } catch (_e) {
            return null; // Templates are optional — a load failure never blocks the workspace.
        }
    }, []);

    // Load on mount + whenever the parent bumps reloadSignal (e.g. after the
    // first-run wizard creates the very first template from the dashboard).
    useEffect(() => {
        load();
    }, [load, reloadSignal]);

    // Auto-load the DEFAULT template exactly once: select it and push its
    // snapshot into the workspace so a returning creator lands on their default.
    // Silent (no toast). Fails closed for a music template with an unloaded
    // catalog — the creator can simply re-pick it.
    useEffect(() => {
        if (autoAppliedRef.current) return;
        if (!defaultTemplateId || presets.length === 0) return;
        const d = presets.find((p) => p.id === defaultTemplateId);
        if (!d) return;
        autoAppliedRef.current = true;
        setSelectedId(d.id);
        onApply?.(d.settings);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presets, defaultTemplateId]);

    // Direct MENU application: selecting a template applies it right away.
    // Re-selecting the SAME template re-applies its snapshot (discarding edits).
    const applySelect = (p, { silent = false } = {}) => {
        const seq = ++applySeqRef.current;
        setSelectedId(p.id);
        setMenuOpen(false);
        const applied = onApply?.(p.settings);
        if (seq !== applySeqRef.current) return; // superseded by a newer selection
        if (!silent && applied !== false) toast.success(`Applied "${p.name}".`);
    };

    // Overwrite the selected template's SETTINGS in place (name unchanged) so a
    // creator can save edits before uploading. Preserves Commit 1 revision CAS.
    // Returns { ok, retryable, message? } so parent-driven inline saves (via
    // saveEditsSignal) can react to success/failure without duplicating the
    // network path.
    const runSaveChanges = useCallback(async () => {
        if (!selected) return { ok: false, retryable: false, message: "No template selected." };
        setBusy(true);
        try {
            const { data } = await api.patch(`${ENDPOINT}/${selected.id}`, {
                settings, revision: selected.revision,
            });
            const p = data.preset;
            setPresets((prev) => prev.map((x) => (x.id === p.id ? p : x)).sort(byNameAsc));
            return { ok: true };
        } catch (e) {
            if (detailCode(e) === "revision_conflict") {
                load();
                return { ok: false, retryable: true, message: "This template changed elsewhere. Reloading…" };
            }
            return { ok: false, retryable: true, message: detailMessage(e, "Could not save changes.") };
        } finally {
            setBusy(false);
        }
    }, [selected, settings, load]);

    const saveChanges = async () => {
        const res = await runSaveChanges();
        if (res.ok) toast.success(`Saved changes to "${selected.name}".`);
        else if (res.message) toast.error(res.message);
    };

    // Parent-driven inline save (compact Dashboard edit view). A bumped
    // saveEditsSignal invokes the SAME owner-scoped revision-aware PATCH and
    // reports the outcome via onSaveEditsResult so the parent can close/keep
    // its edit view.
    const inlineSaveSeqRef = useRef(0);
    useEffect(() => {
        if (saveEditsSignal <= 0) return;
        if (saveEditsSignal === inlineSaveSeqRef.current) return;
        inlineSaveSeqRef.current = saveEditsSignal;
        (async () => {
            const res = await runSaveChanges();
            onSaveEditsResult?.(res);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveEditsSignal]);

    const makeDefault = async (p) => {
        setMenuOpen(false);
        setBusy(true);
        try {
            await api.put(DEFAULT_ENDPOINT, {
                default_template_id: p.id,
                expected_default_template_id: defaultTemplateId || null,
            });
            toast.success(`"${p.name}" is now your default.`);
            onDefaultChanged?.();
        } catch (e) {
            if (detailCode(e) === "default_conflict") {
                toast.error("Your default changed elsewhere. Reloading…");
                onDefaultChanged?.();
            } else {
                toast.error(detailMessage(e, "Could not set the default template."));
            }
        } finally {
            setBusy(false);
        }
    };

    const confirmDelete = (p) => {
        setReplaceFor(null);
        setMenuOpen(false);
        setDeleteFor(p);
    };

    const runDelete = async (target, replacementId = null) => {
        setBusy(true);
        try {
            const { data } = replacementId
                ? await api.delete(`${ENDPOINT}/${target.id}`, {
                    data: { replacement_default_id: replacementId },
                })
                : await api.delete(`${ENDPOINT}/${target.id}`);
            setPresets((prev) => prev.filter((x) => x.id !== target.id));
            setSelectedId((prev) => (prev === target.id ? "" : prev));
            setDeleteFor(null);
            setReplaceFor(null);
            if (data && Object.prototype.hasOwnProperty.call(data, "default_template_id")) {
                onDefaultChanged?.();
            }
            toast.success("Template deleted.");
        } catch (e) {
            if (detailCode(e) === "replacement_default_required") {
                // Deleting the current default while 2+ others remain — the
                // customer must explicitly pick the successor default first.
                setDeleteFor(null);
                setReplaceFor({
                    preset: target,
                    others: presets.filter((x) => x.id !== target.id),
                });
            } else {
                toast.error(detailMessage(e, "Could not delete the template."));
            }
        } finally {
            setBusy(false);
        }
    };

    const openCreate = () => {
        setMenuOpen(false);
        setWizard({
            open: true, mode: "create", editId: null, editRevision: null,
            initialSettings: settings, initialName: "",
        });
    };
    const openEdit = (p) => {
        setMenuOpen(false);
        setWizard({
            open: true, mode: "edit", editId: p.id, editRevision: p.revision,
            initialSettings: p.settings, initialName: p.name,
        });
    };
    const closeWizard = () => setWizard((w) => ({ ...w, open: false }));

    // Persist a wizard result. EDIT → PATCH the exact owned id (revision CAS).
    // CREATE → POST a genuinely new template; an explicit user Overwrite (by id)
    // PATCHes that exact id. A POST name_conflict is NEVER auto-converted to a
    // PATCH — it is surfaced so the creator explicitly chooses Overwrite / rename.
    const handleWizardFinish = useCallback(async ({ settings: snap, name, overwriteId }) => {
        const isEdit = wizard.mode === "edit";
        const patchId = isEdit ? wizard.editId : overwriteId;
        if (patchId) {
            const targetRev = isEdit
                ? wizard.editRevision
                : (presets.find((p) => p.id === patchId) || {}).revision;
            if (targetRev == null) {
                await load();
                return { ok: false, nameConflict: true };
            }
            try {
                const { data } = await api.patch(`${ENDPOINT}/${patchId}`, {
                    name, settings: snap, revision: targetRev,
                });
                const p = data.preset;
                setPresets((prev) => prev.map((x) => (x.id === p.id ? p : x)).sort(byNameAsc));
                setSelectedId(p.id);
                onApply?.(p.settings);
                closeWizard();
                toast.success(`Saved "${p.name}".`);
                return { ok: true };
            } catch (e) {
                const c = detailCode(e);
                if (c === "name_conflict") return { ok: false, nameConflict: true };
                if (c === "revision_conflict") {
                    toast.error("This template changed elsewhere. Reloading…");
                    load();
                    closeWizard();
                    return { ok: false };
                }
                toast.error(detailMessage(e, "Could not save the template."));
                return { ok: false };
            }
        }
        // Genuinely new template → POST.
        try {
            const { data } = await api.post(ENDPOINT, { name, settings: snap });
            const p = data.preset;
            setPresets((prev) => [...prev, p].sort(byNameAsc));
            setSelectedId(p.id);
            onApply?.(p.settings);
            closeWizard();
            toast.success(`Saved "${p.name}".`);
            return { ok: true };
        } catch (e) {
            const c = detailCode(e);
            if (c === "name_conflict") {
                await load(); // surface the collision so the wizard shows Overwrite
                return { ok: false, nameConflict: true };
            }
            toast.error(detailMessage(e, "Could not save the template."));
            return { ok: false };
        }
    }, [wizard.mode, wizard.editId, wizard.editRevision, presets, onApply, load]);

    const ctlDisabled = disabled || busy;
    const btn =
        "inline-flex min-h-11 items-center gap-1.5 rounded-full border px-3.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60 disabled:cursor-not-allowed disabled:opacity-40";
    const btnGhost = `${btn} border-white/12 bg-white/[0.04] text-white/80 hover:border-white/25 hover:text-white`;
    const btnPrimary = `${btn} border-purple-400/50 bg-purple-500/15 text-purple-100 hover:bg-purple-500/25`;

    const triggerLabel = selected
        ? selected.name
        : presets.length > 0
            ? "Choose a template"
            : "No templates yet";

    return (
        <div
            data-testid="workflow-preset-control"
            className={
                compact
                    ? "mt-6 flex flex-col gap-3"
                    : "mt-6 flex flex-col gap-3 rounded-lg border border-white/12 bg-surface-panel p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5"
            }
        >
            {!compact && (
                <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg border border-cyan-400/40 bg-cyan-500/10">
                        <Bookmark className="h-5 w-5 text-cyan-300" />
                    </span>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold uppercase tracking-wide text-white/70">
                            Workflow Templates
                        </p>
                        {presets.length === 0 && (
                            <p
                                data-testid="workflow-preset-empty"
                                className="mt-0.5 text-sm text-white/45"
                            >
                                No saved templates yet — create your first below.
                            </p>
                        )}
                    </div>
                </div>
            )}

            <div className={compact ? "w-full" : "flex flex-wrap items-center gap-2"}>
                {/* In-app template menu (replaces the native <select>). In
                    compact mode the WHOLE thin bar is the trigger. */}
                <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            data-testid="workflow-preset-trigger"
                            disabled={ctlDisabled}
                            aria-label="Choose a workflow template"
                            className={
                                compact
                                    ? "flex min-h-14 w-full items-center justify-between gap-3 rounded-lg border border-white/15 bg-surface-panel px-4 py-2 text-left outline-none transition-colors hover:border-purple-400/60 focus-visible:ring-2 focus-visible:ring-purple-400/60 disabled:cursor-not-allowed disabled:opacity-50"
                                    : "inline-flex min-h-11 min-w-[13rem] items-center justify-between gap-2 rounded-full border border-white/15 bg-black/40 px-4 text-sm text-white outline-none transition-colors hover:border-purple-400/60 focus-visible:ring-2 focus-visible:ring-purple-400/60 disabled:cursor-not-allowed disabled:opacity-50"
                            }
                        >
                            {compact ? (
                                <>
                                    <span className="flex min-w-0 items-center gap-3">
                                        <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg border border-cyan-400/40 bg-cyan-500/10">
                                            <Bookmark className="h-4 w-4 text-cyan-300" />
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block text-[11px] font-semibold uppercase tracking-wide text-white/50">
                                                Template
                                            </span>
                                            <span className="block truncate text-sm font-semibold text-white">
                                                {triggerLabel}
                                            </span>
                                        </span>
                                    </span>
                                    <span className="flex flex-none items-center gap-2">
                                        {selected && selected.id === defaultTemplateId && (
                                            <span className="hidden items-center gap-1 rounded-full border border-amber-300/30 bg-amber-400/10 px-2 py-0.5 text-xs font-semibold text-amber-200 sm:inline-flex">
                                                <Star className="h-3 w-3 fill-amber-300 text-amber-300" />
                                                Default
                                            </span>
                                        )}
                                        <ChevronDown className="h-4 w-4 flex-none text-white/50" />
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="flex min-w-0 items-center gap-1.5 truncate">
                                        <span className="truncate">{triggerLabel}</span>
                                        {edited && (
                                            <span
                                                data-testid="workflow-preset-edited-badge"
                                                className="flex-none rounded-full border border-amber-300/40 bg-amber-400/10 px-2 py-0.5 text-xs font-semibold text-amber-200"
                                            >
                                                • Edited
                                            </span>
                                        )}
                                    </span>
                                    <ChevronDown className="h-4 w-4 flex-none text-white/50" />
                                </>
                            )}
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        data-testid="workflow-preset-menu"
                        align="end"
                        className="min-w-[15rem] max-w-[min(24rem,calc(100vw-2rem))] border-white/12 bg-surface-raised text-white"
                    >
                        {presets.length > 0 && (
                            <DropdownMenuLabel className="text-white/50">
                                Your templates
                            </DropdownMenuLabel>
                        )}
                        {presets.map((p) => (
                            <DropdownMenuItem
                                key={p.id}
                                data-testid={`workflow-preset-option-${p.id}`}
                                onSelect={() => applySelect(p)}
                                className="flex items-center justify-between gap-2 focus:bg-white/10"
                            >
                                <span className="truncate">{p.name}</span>
                                <span className="flex flex-none items-center gap-1">
                                    {p.id === defaultTemplateId && (
                                        <Star
                                            data-testid={`workflow-preset-default-star-${p.id}`}
                                            className="h-3.5 w-3.5 fill-amber-300 text-amber-300"
                                        />
                                    )}
                                    {p.id === selectedId && (
                                        <Check className="h-4 w-4 text-purple-300" />
                                    )}
                                </span>
                            </DropdownMenuItem>
                        ))}
                        {presets.length > 0 && <DropdownMenuSeparator className="bg-white/10" />}
                        <DropdownMenuItem
                            data-testid="workflow-preset-create"
                            onSelect={openCreate}
                            className="min-h-11 gap-1.5 text-[13px] text-white/70 focus:bg-white/10 [&>svg]:size-3.5"
                        >
                            <Plus className="h-3.5 w-3.5" /> Create new template
                        </DropdownMenuItem>
                        {selected && (
                            <>
                                {compact && typeof onInlineEdit === "function" && (
                                    <DropdownMenuItem
                                        data-testid="dashboard-edit-template-button"
                                        onSelect={() => onInlineEdit()}
                                        className="min-h-11 gap-1.5 text-[13px] text-white/70 focus:bg-white/10 [&>svg]:size-3.5"
                                    >
                                        <Pencil className="h-3.5 w-3.5" /> Edit template
                                    </DropdownMenuItem>
                                )}
                                {!compact && (
                                    <DropdownMenuItem
                                        data-testid="workflow-preset-edit"
                                        onSelect={() => openEdit(selected)}
                                        className="focus:bg-white/10"
                                    >
                                        <Pencil className="h-4 w-4 flex-none" />
                                        <span className="truncate">Edit “{selected.name}”</span>
                                    </DropdownMenuItem>
                                )}
                                {selected.id !== defaultTemplateId && (
                                    <DropdownMenuItem
                                        data-testid="workflow-preset-set-default"
                                        onSelect={() => makeDefault(selected)}
                                        className="focus:bg-white/10"
                                    >
                                        <Star className="h-4 w-4" /> Set as default
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                    data-testid="workflow-preset-delete"
                                    onSelect={() => confirmDelete(selected)}
                                    className="min-h-11 gap-1.5 text-[13px] text-red-300 focus:bg-red-500/15 [&>svg]:size-3.5"
                                >
                                    <Trash2 className="h-3.5 w-3.5 flex-none" />
                                    <span className="truncate">Delete “{selected.name}”</span>
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>

                {/* Save edits back to the selected template before uploading.
                    Hidden in compact mode — the Dashboard-owned Save changes
                    command in the edit-view sticky bar drives the same PATCH
                    via saveEditsSignal. */}
                {edited && selected && !compact && (
                    <button
                        type="button"
                        data-testid="workflow-preset-save-changes"
                        onClick={saveChanges}
                        disabled={ctlDisabled}
                        className={btnPrimary}
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        Save changes
                    </button>
                )}

                {/* Always-available quick action to build a brand-new template.
                    Hidden in compact mode; Create-new stays reachable inside the
                    template menu ("Create new template" item). */}
                {!compact && (
                    <button
                        type="button"
                        data-testid="workflow-preset-new"
                        onClick={openCreate}
                        disabled={ctlDisabled}
                        className={btnGhost}
                    >
                        <Plus className="h-4 w-4" /> New
                    </button>
                )}
            </div>

            {/* Inline delete confirmation */}
            {deleteFor && (
                <div className="flex flex-wrap items-center gap-2">
                    <span
                        data-testid="workflow-preset-delete-prompt"
                        className="text-sm text-white/75"
                    >
                        Delete "{deleteFor.name}"?
                    </span>
                    <button
                        type="button"
                        data-testid="workflow-preset-delete-confirm"
                        onClick={() => runDelete(deleteFor)}
                        disabled={busy}
                        className={`${btn} border-red-400/50 bg-red-500/15 text-red-200 hover:bg-red-500/25`}
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        Confirm
                    </button>
                    <button
                        type="button"
                        data-testid="workflow-preset-delete-cancel"
                        onClick={() => setDeleteFor(null)}
                        disabled={busy}
                        className={btnGhost}
                    >
                        <X className="h-4 w-4" /> Cancel
                    </button>
                </div>
            )}

            {/* Replacement-default picker — required when deleting the current
                default while two or more other templates remain. */}
            {replaceFor && (
                <div className="flex w-full flex-col gap-2 rounded-lg border border-amber-300/30 bg-amber-400/[0.06] p-3">
                    <span
                        data-testid="workflow-preset-replacement-prompt"
                        className="text-sm text-amber-100/90"
                    >
                        “{replaceFor.preset.name}” is your default. Pick which template
                        becomes the new default:
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                        {replaceFor.others.map((o) => (
                            <button
                                key={o.id}
                                type="button"
                                data-testid={`workflow-preset-replacement-${o.id}`}
                                onClick={() => runDelete(replaceFor.preset, o.id)}
                                disabled={busy}
                                className={btnPrimary}
                            >
                                <Star className="h-4 w-4" /> {o.name}
                            </button>
                        ))}
                        <button
                            type="button"
                            data-testid="workflow-preset-replacement-cancel"
                            onClick={() => setReplaceFor(null)}
                            disabled={busy}
                            className={btnGhost}
                        >
                            <X className="h-4 w-4" /> Cancel
                        </button>
                    </div>
                </div>
            )}

            <FirstRunTemplateWizard
                open={wizard.open}
                mode={wizard.mode}
                initialSettings={wizard.initialSettings}
                initialName={wizard.initialName}
                existingPresets={
                    wizard.mode === "edit"
                        ? presets.filter((p) => p.id !== wizard.editId)
                        : presets
                }
                captionStyles={wizardCatalog.captionStyles || []}
                customCaptionSelection={wizardCatalog.customCaptionSelection || null}
                customCaptionAvailable={!!wizardCatalog.customCaptionAvailable}
                musicTracks={wizardCatalog.musicTracks || []}
                musicLoading={!!wizardCatalog.musicLoading}
                musicError={!!wizardCatalog.musicError}
                musicLoadedOnce={!!wizardCatalog.musicLoadedOnce}
                onRequestMusicLoad={wizardCatalog.onRequestMusicLoad}
                onCancel={closeWizard}
                onFinish={handleWizardFinish}
            />
        </div>
    );
};

export default WorkflowPresetControl;
